import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { OverlayPortal } from './OverlayPortal';
import { useAnchoredPosition } from '../../hooks/useAnchoredPosition';
import { ConfirmDialog } from './ConfirmDialog';

export interface ActionMenuItem {
  key: string;
  label: string;
  onSelect: () => void;
  icon?: React.ReactNode;
  disabled?: boolean;
  /** Renders the item in the error/danger color. */
  destructive?: boolean;
  /** When set, selecting the item opens a ConfirmDialog first; onSelect fires only on confirm. */
  confirm?: {
    title: string;
    description?: string;
    confirmLabel?: string;
  };
}

export interface ActionMenuProps {
  items: ActionMenuItem[];
  /** aria-label for the trigger button. */
  triggerLabel?: string;
  /** Which edge of the trigger the menu aligns to. */
  align?: 'start' | 'end';
  disabled?: boolean;
  /** data-testid forwarded to the trigger; the menu gets `${testId}-menu`. */
  testId?: string;
  /** Notified on open/close — e.g. so a hover-revealed trigger can stay visible while open. */
  onOpenChange?: (open: boolean) => void;
}

const Wrapper = styled.div`
  position: relative;
  display: inline-flex;
`;

const TriggerButton = styled.button<{ $open: boolean }>`
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  border: none;
  background: ${({ theme, $open }) => ($open ? theme.colors.backgroundTertiary : 'transparent')};
  color: ${({ theme }) => theme.colors.textSecondary};
  cursor: pointer;
  transition: background ${({ theme }) => theme.transitions.fast};

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.backgroundTertiary};
    color: ${({ theme }) => theme.colors.text};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 2px;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Menu = styled.div<{ $top: number; $left: number; $transform: string }>`
  position: fixed;
  top: ${({ $top }) => $top}px;
  left: ${({ $left }) => $left}px;
  transform: ${({ $transform }) => $transform};
  z-index: ${({ theme }) => theme.zIndex.dropdown};
  min-width: 180px;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  box-shadow: ${({ theme }) => theme.shadows.lg};
  padding: ${({ theme }) => theme.spacing.xs} 0;
  overflow: hidden;
`;

const MenuItem = styled.button<{ $destructive?: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  text-align: left;
  border: none;
  background: transparent;
  color: ${({ theme, $destructive }) => ($destructive ? theme.colors.error : theme.colors.text)};
  cursor: pointer;
  transition: background ${({ theme }) => theme.transitions.fast};

  &:hover:not(:disabled),
  &:focus-visible {
    background: ${({ theme }) => theme.colors.surfaceHover};
    outline: none;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

/**
 * Single dropdown-trigger + menu used for all row/detail/page actions.
 * Replaces scattered standalone action buttons. Destructive items with a
 * `confirm` config spawn a <ConfirmDialog> instead of firing immediately.
 * See docs/03-architecture/app-shell.md.
 */
export const ActionMenu: React.FC<ActionMenuProps> = ({
  items,
  triggerLabel = 'Actions',
  align = 'end',
  disabled = false,
  testId,
  onOpenChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [pendingConfirm, setPendingConfirm] = useState<ActionMenuItem | null>(null);
  const [confirmBusy, setConfirmBusy] = useState(false);
  const [focusIndex, setFocusIndex] = useState(-1);

  const wrapperRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const position = useAnchoredPosition(triggerRef, 'bottom', isOpen, 6, align, menuRef);

  const close = useCallback(() => {
    setIsOpen(false);
    setFocusIndex(-1);
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (wrapperRef.current?.contains(target)) return;
      // The menu is PORTALLED, so it is not inside wrapperRef. Checking the
      // menu container (not just the item buttons) means a mousedown on the
      // menu's own padding or the gap between items no longer closes it.
      if (menuRef.current?.contains(target)) return;
      close();
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, close]);

  useEffect(() => {
    if (focusIndex >= 0) itemRefs.current[focusIndex]?.focus();
  }, [focusIndex]);

  // Drop refs for items that no longer exist — otherwise a shrinking `items`
  // array leaves detached nodes behind that outlive their menu.
  itemRefs.current.length = items.length;

  const didMountRef = useRef(false);
  useEffect(() => {
    // Skip the mount call: every row in a table renders an ActionMenu, so
    // firing onOpenChange(false) on mount meant N redundant state updates
    // before the user had touched anything.
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    onOpenChange?.(isOpen);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const enabledIndexes = items.reduce<number[]>((acc, item, idx) => {
    if (!item.disabled) acc.push(idx);
    return acc;
  }, []);

  const moveFocus = (direction: 1 | -1) => {
    if (enabledIndexes.length === 0) return;
    const currentPos = enabledIndexes.indexOf(focusIndex);
    const nextPos =
      currentPos === -1
        ? direction === 1
          ? 0
          : enabledIndexes.length - 1
        : (currentPos + direction + enabledIndexes.length) % enabledIndexes.length;
    setFocusIndex(enabledIndexes[nextPos]);
  };

  const activate = (item: ActionMenuItem) => {
    if (item.disabled) return;
    if (item.confirm) {
      setPendingConfirm(item);
      close();
      return;
    }
    close();
    triggerRef.current?.focus();
    item.onSelect();
  };

  const handleTriggerKeyDown = (e: React.KeyboardEvent) => {
    if (disabled) return;
    if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setIsOpen(true);
      setFocusIndex(enabledIndexes[0] ?? -1);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setIsOpen(true);
      setFocusIndex(enabledIndexes[enabledIndexes.length - 1] ?? -1);
    }
  };

  const handleMenuKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      moveFocus(1);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      moveFocus(-1);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      close();
      triggerRef.current?.focus();
    } else if (e.key === 'Tab') {
      close();
    }
  };

  const handleConfirm = async () => {
    if (!pendingConfirm) return;
    setConfirmBusy(true);
    try {
      // `onSelect` is typed `() => void`, but callers routinely hand us an
      // async mutation. Awaiting the returned value keeps the dialog open
      // (and the confirm button in its busy state) until the work actually
      // settles — previously it closed instantly and `busy` never rendered.
      await (pendingConfirm.onSelect() as void | Promise<void>);
    } finally {
      setConfirmBusy(false);
      setPendingConfirm(null);
    }
  };

  return (
    <Wrapper ref={wrapperRef}>
      <TriggerButton
        ref={triggerRef}
        type="button"
        $open={isOpen}
        disabled={disabled || items.length === 0}
        aria-label={triggerLabel}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        data-testid={testId}
        onClick={() => (isOpen ? close() : setIsOpen(true))}
        onKeyDown={handleTriggerKeyDown}
      >
        <svg width="18" height="18" viewBox="0 0 20 20" fill="currentColor">
          <circle cx="10" cy="4" r="1.6" />
          <circle cx="10" cy="10" r="1.6" />
          <circle cx="10" cy="16" r="1.6" />
        </svg>
      </TriggerButton>
      {isOpen && (
        <OverlayPortal>
          <Menu
            ref={menuRef}
            role="menu"
            aria-label={triggerLabel}
            $top={position.top}
            $left={position.left}
            $transform={position.transform}
            onKeyDown={handleMenuKeyDown}
            data-testid={testId ? `${testId}-menu` : undefined}
          >
            {items.map((item, idx) => (
              <MenuItem
                key={item.key}
                ref={(el) => {
                  itemRefs.current[idx] = el;
                }}
                type="button"
                role="menuitem"
                tabIndex={-1}
                disabled={item.disabled}
                $destructive={item.destructive}
                onClick={() => activate(item)}
                data-testid={testId ? `${testId}-item-${item.key}` : undefined}
              >
                {item.icon}
                {item.label}
              </MenuItem>
            ))}
          </Menu>
        </OverlayPortal>
      )}
      {pendingConfirm?.confirm && (
        <ConfirmDialog
          isOpen
          onClose={() => setPendingConfirm(null)}
          onConfirm={handleConfirm}
          title={pendingConfirm.confirm.title}
          description={pendingConfirm.confirm.description}
          confirmLabel={pendingConfirm.confirm.confirmLabel}
          destructive={pendingConfirm.destructive}
          busy={confirmBusy}
          testIdPrefix={`candeza-confirm-${pendingConfirm.key}`}
        />
      )}
    </Wrapper>
  );
};
